'use client'
import { memo, useCallback, useEffect, useState } from 'react'
import type { FC } from 'react'
import  styled, { css } from 'styled-components'
import { MODALS } from '@/context'
import { useMount, useModal, useTranslator } from '@/hooks'
import {  getP12, getP18, getP50, mq, rgbAlpha, vw } from '@/styles'
import { Modal } from './Modal'
import { fetchAPI } from '@/api'

type TypeSpecSheetStatus = {
  specCode: string
  retryCount: number
  url: string | null
  hasError: boolean
}

export const ModalPdfSpecSheet: FC = memo(() => {
  const { isActive, updatePayload, payload } = useModal(MODALS.PDP_SPEC_PDF)
  const { t } = useTranslator()
  const isMount = useMount()
  const [status, setStatus] = useState<TypeSpecSheetStatus | null>(null)
  const [retryCount, setRetryCount] = useState(0)
  const specCode = (payload?.code ?? payload?.sageId) as string | undefined
  const canRequest = Boolean(isActive && specCode)
  const isCurrent = Boolean(canRequest && status?.specCode === specCode && status.retryCount === retryCount)
  const url = isCurrent && status?.url ? status.url : null
  const hasError = Boolean(isCurrent && status?.hasError)
  const isLoading = Boolean(canRequest && !isCurrent)
  const close = () => {
    updatePayload({ close: true, open: false, payload: null })
  }

  useEffect(() => {
    if (!isActive || !specCode) return

    let isCancelled = false
    const requestCode = specCode
    const requestRetry = retryCount

    const markFailed = () => {
      if (isCancelled) return
      setStatus({ specCode: requestCode, retryCount: requestRetry, url: null, hasError: true })
    }

    const load = async () => {
      try {
        const res = await fetchAPI('get-pdf-spec-sheet-url', { lang: 'en', slug: requestCode }, {
          method: 'GET',
          cache: 'no-store',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
        }) as Response

        if (isCancelled) return
        if (!res.ok) {
          markFailed()
          return
        }

        const { url: specSheetUrl } = await res.json()
        if (isCancelled) return
        if (!specSheetUrl) {
          markFailed()
          return
        }

        setStatus({
          specCode: requestCode,
          retryCount: requestRetry,
          url: specSheetUrl,
          hasError: false,
        })
      } catch (error) {
        console.warn('spec sheet url failed', error)
        markFailed()
      }
    }

    void load()

    return () => {
      isCancelled = true
    }
  }, [isActive, specCode, retryCount])

  const onClickPdf = useCallback(() => {
    if (url) {
      const newTab = window.open(url, '_blank')
      if (newTab) newTab.focus()
      return
    }
    if (hasError) setRetryCount((count) => count + 1)
  }, [url, hasError])

  const isDisabled = !canRequest || isLoading || (!url && !hasError)

  return isMount && (
    <Modal isActive={isActive} close={close} title={t('project.modals.pdp_spec_pdf.title') as string} xAlign='center' size={'s'} dialogStyles={dialogStyles} hasHeader={true} zIndex={8000} hasToClose={true}>
      <Main>
        <button className='btn--download' type='button' disabled={isDisabled} onClick={onClickPdf}>{isLoading ? t('status.downloading') : t('actions.download')}</button>
      </Main>
    </Modal>

  )
})

const dialogStyles = css`
  border: 1px solid ${rgbAlpha('--color-text', .3)};
  box-shadow: unset;
  height: 100%;
  margin: 0 auto;

  ${mq.greaterThan('tablet')}{
    height: 200px;
    overflow-y: hidden;
  }


  .modal__body {
    align-items: center;
    background-color:rgb(230, 227, 221);
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
  }

  .btn--download {
    align-items: center;
    background-color: var(--color-text);
    border: 1px solid var(--color-text);
    color: var(--color-background);
    display: flex;
    ${getP12()}
    justify-content: center;
    min-width: 150px;
    padding: 15px 30px;
    transition: background-color 250ms ease-in-out, color 250ms ease-in-out;
    width: fit-content;

    &:hover:not(:disabled) {
      background-color: var(--color-background);
      color: var(--color-text);
    }

    &:disabled {
      cursor: progress;
      opacity: .6;
    }
  }
`

const Main = styled.main`
  position: relative;

  h2 {
    ${getP50()}
    margin-bottom: ${vw(40, 'mobile')};

    ${mq.greaterThan('tablet')}{
      margin-bottom: ${vw(90, 'desktop')};
    }

  }

  input, legend {
    ${getP18()}
  }

`
